import { URL_BASE } from "../../models/url"
import { safeStorage } from "../../helpers/safeStorage"

/**
 * Persistenz der fromUrl über den Login-/Passwordless-Flow hinweg.
 *
 * Hintergrund: Die Ziel-URL nach dem Login wird per Location-State
 * ({ fromUrl: { pathname } }) übergeben. Beim Magic-Link-Login öffnet der
 * E-Mail-Link aber eine NEUE Navigation — der Location-State ist dann immer
 * weg und der User landete bisher auf der Startseite statt z.B. zurück in
 * seiner Buchung. Deshalb wird die fromUrl beim Start des Flows zusätzlich
 * in safeStorage abgelegt und nach erfolgreichem Login wieder entfernt.
 */
export const LOGIN_FROM_URL_STORAGE_KEY = "loginFromUrl"

/**
 * TTL für den persistierten Wert (30 Minuten).
 *
 * Warum TTL statt Räumen-beim-Mount: Der Magic-Link öffnet eine NEUE Navigation —
 * dabei rendert eine Login-Route OHNE Location-State, BEVOR der Flow abgeschlossen
 * ist. Würde so ein Mount den Storage räumen, ginge genau der Wert verloren, den
 * der Magic-Link-Flow noch braucht. Die TTL lässt den Flow (Minuten) intakt und
 * neutralisiert trotzdem tagealte Reste abgebrochener Logins, die sonst einen
 * späteren regulären Login (direkt /login, ohne State) an ein veraltetes
 * Buchungsziel umleiten würden.
 */
export const LOGIN_FROM_URL_TTL_MS = 30 * 60 * 1000

// search (z.B. "?tab=past") überlebt den Login-Flow mit — history.push(fromUrl)
// nimmt das Objekt inkl. search unverändert an.
export type FromUrl = { pathname: string; search?: string }

/**
 * Optionale Kurs-ID für den Passwordless-Mailkontext.
 *
 * Nur echte Kursrouten dürfen das Backend-Feld `courseId` befüllen. Bei
 * anderen Rücksprungzielen (insbesondere `/interest/:id`) wäre das letzte
 * Pfadsegment keine Kurs-ID und könnte serverseitig zu einer falschen
 * Kursauflösung führen.
 */
export function getCourseIdFromLoginFromUrl(fromUrl: FromUrl): string | undefined {
    const courseDetailsMatch = fromUrl.pathname.match(/^\/details\/([^/]+)\/?$/)
    if (courseDetailsMatch) return courseDetailsMatch[1]

    const bookingSummaryMatch = fromUrl.pathname.match(/^\/booking\/summary\/([^/]+)\/?$/)
    return bookingSummaryMatch?.[1]
}

/** Persistiert die fromUrl beim Start des Login-/Passwordless-Flows (mit Timestamp für die TTL). */
export function persistLoginFromUrl(fromUrl?: FromUrl): void {
    if (fromUrl?.pathname) {
        safeStorage.setItem(LOGIN_FROM_URL_STORAGE_KEY, JSON.stringify({ pathname: fromUrl.pathname, search: fromUrl.search || undefined, ts: Date.now() }))
    }
}

/**
 * Liest die persistierte fromUrl (defensiv gegen kaputte/fremde Werte).
 * Abgelaufene Einträge (älter als LOGIN_FROM_URL_TTL_MS) werden ignoriert
 * und direkt aus dem Storage entfernt. Einträge im alten Format ohne
 * Timestamp gelten ebenfalls als abgelaufen — ihr Alter ist nicht bestimmbar.
 */
export function readPersistedLoginFromUrl(): FromUrl | undefined {
    try {
        const raw = safeStorage.getItem(LOGIN_FROM_URL_STORAGE_KEY)
        if (!raw) return undefined
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed.pathname === "string") {
            const isFresh = typeof parsed.ts === "number" && Date.now() - parsed.ts <= LOGIN_FROM_URL_TTL_MS
            if (!isFresh) {
                clearPersistedLoginFromUrl()
                return undefined
            }
            return { pathname: parsed.pathname, search: typeof parsed.search === "string" ? parsed.search : undefined }
        }
        return undefined
    } catch {
        return undefined
    }
}

/** Entfernt die persistierte fromUrl (nach Verwendung aufrufen). */
export function clearPersistedLoginFromUrl(): void {
    safeStorage.removeItem(LOGIN_FROM_URL_STORAGE_KEY)
}

/**
 * Löst die Ziel-URL nach dem Login auf.
 * Priorität: Location-State > persistierter Wert > URL_BASE.
 */
export function resolveLoginFromUrl(stateFromUrl?: FromUrl): FromUrl {
    return stateFromUrl ?? readPersistedLoginFromUrl() ?? { pathname: URL_BASE }
}
